import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useGenericBi } from './use-generic-bi';
import { usePageLoadBi } from './use-page-load-bi';
import { queryKeys } from './query-client';
import { acknowledgeAllNotifications, acknowledgeNotificationTap, currentChatOwner, markLegacyNotificationsRead, NotificationSnapshotUnsupportedError, postChatRead, ReadSnapshotExpiredError } from './chat-reads';
import { useRecipient } from '../components/settings/shared/settings-queries';
import { useUnreadNotifications } from './use-unread-notifications';
import { notificationPrepareExpired } from './chat-session';

const PAGE = 'notifications';

export interface AppNotification {
  id: string;
  title: string;
  body: string;
  date: string | null;
  read: boolean;
  conversationId: string | null;
  /** Raw mobile deep link, for rows that are not about a conversation. See notifications-feed.ts. */
  deeplink: string | null;
}

interface FeedPage {
  notifications: AppNotification[];
  nextCursor: string | null;
}

export async function fetchPage(cursor: string | null): Promise<FeedPage> {
  const url = cursor
    ? `${API_BASE}/api/notifications?cursor=${encodeURIComponent(cursor)}`
    : `${API_BASE}/api/notifications`;
  const res = await apiFetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as { notifications?: AppNotification[]; nextCursor?: string | null };
  return { notifications: data.notifications ?? [], nextCursor: data.nextCursor ?? null };
}

export type NotificationsStatus = 'loading' | 'error' | 'ready';

export function useNotificationsFeed() {
  usePageLoadBi({ page_name: PAGE });
  const { logPageClickBi } = useGenericBi('notifications');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const recipient = useRecipient();
  const owner = recipient.data?.email && recipient.data.metaSiteId ? JSON.stringify([recipient.data.email, recipient.data.metaSiteId]) : null;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState(false);
  const [clearExpired, setClearExpired] = useState(false);
  const [tapExpired, setTapExpired] = useState(false);
  const [tapError, setTapError] = useState<AppNotification | null>(null);
  const clearPending = useRef<{ owner: string; requestId: string; createdAt: number; snapshotToken?: string; prepareAttempted?: boolean } | null>(null);
  const totalUnread = useUnreadNotifications();

  const query = useInfiniteQuery({
    queryKey: [...queryKeys.notifications, owner],
    queryFn: ({ pageParam = null }: { pageParam?: string | null }) => fetchPage(pageParam),
    enabled: !!owner,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => { clearPending.current = null; setClearing(false); setClearError(false); setClearExpired(false); setTapExpired(false); setTapError(null); }, [owner]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const seen = new Set<string>();
  const notifications: AppNotification[] = [];
  for (const p of query.data?.pages ?? []) {
    for (const n of p.notifications) {
      if (!seen.has(n.id)) {
        seen.add(n.id);
        notifications.push(n);
      }
    }
  }

  const markAllRead = async () => {
    if (!owner || clearing) return;
    logPageClickBi({ page_name: PAGE, button_name: 'mark_all_read' });
    setClearing(true);
    setClearError(false);
    setClearExpired(false);
    const operation = clearPending.current ?? { owner, requestId: crypto.randomUUID(), createdAt: Date.now() };
    clearPending.current = operation;
    const mayUseLegacy = !operation.prepareAttempted;
    const active = () => ownerRef.current === owner && currentChatOwner() === owner && clearPending.current === operation;
    try {
      if (!operation.snapshotToken) {
        if (notificationPrepareExpired(operation.createdAt)) throw new ReadSnapshotExpiredError();
        operation.prepareAttempted = true;
        const prepared = await postChatRead<{ snapshotToken?: string }>({ action: 'notification-read-prepare', requestId: operation.requestId });
        if (notificationPrepareExpired(operation.createdAt)) throw new ReadSnapshotExpiredError();
        if (!prepared.snapshotToken) throw new Error('Missing notification snapshot');
        operation.snapshotToken = prepared.snapshotToken;
      }
      if (!active()) return;
      const receipt = await acknowledgeAllNotifications(operation.snapshotToken, active);
      if (active() && receipt.complete) clearPending.current = null;
      else if (active()) setClearError(true);
    } catch (error) {
      if (active() && error instanceof NotificationSnapshotUnsupportedError && !operation.snapshotToken) {
        // The server explicitly declined snapshot capture before any write.
        // Legacy clear-all covers the whole feed; it never advances chat readers.
        clearPending.current = null;
        if (!mayUseLegacy || notificationPrepareExpired(operation.createdAt)) { setClearError(true); setClearExpired(true); return; }
        try { await markLegacyNotificationsRead(owner); }
        catch { if (ownerRef.current === owner && currentChatOwner() === owner) setClearError(true); }
        return;
      }
      if (active()) {
        setClearError(true);
        if (error instanceof ReadSnapshotExpiredError) { clearPending.current = null; setClearExpired(true); }
      }
    }
    finally { if (ownerRef.current === owner) setClearing(false); }
  };

  const markOpened = (n: AppNotification) => {
    logPageClickBi(
      { page_name: PAGE, button_name: 'open_notification', answer: n.id },
      { entity: { id: n.id, type: 'notification' } },
    );
    if (n.read || !owner) return;
    setTapError(null);
    setTapExpired(false);
    void acknowledgeNotificationTap(owner, n.id, n.date).catch(error => {
      if (ownerRef.current === owner && currentChatOwner() === owner) {
        if (error instanceof ReadSnapshotExpiredError) setTapExpired(true);
        else setTapError(n);
      }
    });
  };

  const status: NotificationsStatus = query.data ? 'ready' : query.status === 'error' ? 'error' : 'loading';

  return {
    status,
    notifications,
    unreadCount: totalUnread,
    clearing,
    clearError,
    clearExpired,
    tapExpired,
    tapError: !!tapError,
    retryTap: () => { if (tapError) markOpened(tapError); },
    loadingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    sentinelRef,
    markAllRead,
    markOpened,
  };
}
